// built to keep data bandwidth costs as low as possible
// caches images locally in local storage using data URLs

import { ImgHTMLAttributes, useEffect, useState } from 'react'
import sha1 from 'sha1'

import styles from './CachedImage.module.scss'

import { useUser, useTranslation } from '../../hooks'

import pages from '../../pages'

import { getUserData } from '../../lib/icp'

import { useHistory, Link } from 'react-router-dom'

const preloadingState: { [key: string]: boolean } = {}

const preloadImage = (cacheKey: string, src: string) => {
  if (!preloadingState[cacheKey]) {
    preloadingState[cacheKey] = true

    const image = new Image()
    let start = 0
    image.setAttribute('crossOrigin', 'anonymous')
    image.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = image.width
      canvas.height = image.height
      const ctx = canvas.getContext('2d')

      if (!ctx) {
        return
      }

      ctx.drawImage(image, 0, 0, image.width, image.height)
      const dataUrl = canvas.toDataURL('image/jpeg')
      try {
        window.localStorage.setItem(cacheKey, dataUrl)
        // console.log(`download & cache of ${src} took ${Date.now() - start}ms...`)
      } catch (e) {
        // console.log('Sometimes storing image fails')
      }
    }
    // console.log(`starting download of ${src}`)
    start = Date.now()
    image.src = src
  }
}

interface IList {
  icon: string
  name: string
  id: string
  url: string
}

interface IUser {
  userName?: string
  id?: string
  profilePhoto?: string
  [propName: string]: string | any
}

const CachedImage = ({ src, ...rest }: ImgHTMLAttributes<any>) => {
  const [cachedSrc, setCachedSrc] = useState('')

  const [user, setUser] = useState<any>(null)

  const { userData, principal } = useUser()

  const { t } = useTranslation()

  const history = useHistory()

  useEffect(() => {
    // do not reload if the src is empty or it is not a full URL
    if (!src || src.substring(0, 4) !== 'http') return
    const cacheKey = sha1(src)
    try {
      const cacheHit = window.localStorage.getItem(cacheKey)

      if (cacheHit) {
        // console.log(`found ${src}, stopped download...`)
        setCachedSrc(cacheHit)
        return
      }
    } catch (e) {
      // sometimes loading fails
    }
    // console.log(`preloading ${src}, downloading...`)
    preloadImage(cacheKey, src)
  }, [src])

  useEffect(() => {
    if (userData) {
      setUser(userData)
    }
    // init_user()
  }, [userData])

  const user_list = [
    {
      icon: 'https://yumi-frontend-assets.s3.ap-east-1.amazonaws.com/yumi/unity/1687315647102_My channel.svg',
      name: 'sideMenuMyChannel',
      id: '1',
      url: `/@${user && user.userName ? user.userName : ''}`,
    },
    {
      icon: 'https://yumi-frontend-assets.s3.ap-east-1.amazonaws.com/yumi/unity/1687315647086_Analytics.svg',
      name: 'sideMenuAnalytics',
      id: '2',
      url: pages.analytics.path,
    },
    {
      icon: 'https://yumi-frontend-assets.s3.ap-east-1.amazonaws.com/yumi/unity/1687315647100_Monetisatio.svg',
      name: 'sideMenuMonetisation',
      id: '3',
      url: pages.monetisation.path,
    },
    {
      icon: 'https://yumi-frontend-assets.s3.ap-east-1.amazonaws.com/yumi/unity/1687315647101_My Account.svg',
      name: 'sideMenuMyAccount',
      id: '5',
      url: pages.account.path,
    },
    {
      icon: 'https://yumi-frontend-assets.s3.ap-east-1.amazonaws.com/yumi/unity/1687315647102_Paid Subscriptions.svg',
      name: 'sideMenuPaidSubscriptions',
      id: '6',
      url: pages.paidSubscriptions.path,
    },
    {
      icon: 'https://yumi-frontend-assets.s3.ap-east-1.amazonaws.com/yumi/unity/1687315647097_Log out.svg',
      name: 'sideMenuLogout',
      id: '7',
      url: pages.logout.path,
    },
  ]

  const intercept_str = (str: string, startIndex: number, endIndex: number) => {
    if (str) {
      const start_str = str.slice(0, startIndex)
      const end_str = str.slice(-endIndex)
      return `${start_str}...${end_str}`
    }
  }
  const copy_text = (str: string) => {
    const input = document.createElement('input')
    document.body.appendChild(input)
    input.setAttribute('value', str)
    input.select()
    if (document.execCommand('copy')) {
      document.execCommand('copy')
      // window.alert('Successful replication')
    }
    document.body.removeChild(input)
  }

  return (
    <div className={styles.cachedimg_wrap}>
      <img className={styles.cach_img} src={cachedSrc || src} {...rest} />
      <div className={styles.avatar_wrap}>
        <div className={styles.avatar_content}>
          <div className={styles.avatar_head}>
            <img
              className={styles.avatar_head_img}
              src={cachedSrc || src}
              alt=""
            />
            <ul className={styles.avatar_head_ul}>
              <li className={styles.avatar_head_ul_name}>
                {user && user.userName ? user.userName : ''}
              </li>
              <li className={styles.avatar_head_ul_bottom}>
                @
                <div className={styles.principal}>
                  {intercept_str(principal, 5, 4)}
                </div>
                <img
                  onClick={() => copy_text(principal)}
                  src="https://yumi-frontend-assets.s3.ap-east-1.amazonaws.com/yumi/unity/1687241091545_copy 1.svg"
                  alt=""
                />
              </li>
            </ul>
          </div>
          <div className={styles.cach_list}>
            {user_list.map((item: any) => {
              return (
                <Link
                  key={item.id}
                  to={item.url}
                  className={styles.cach_list_box}
                >
                  <img
                    className={styles.cach_list_img}
                    src={item.icon}
                    alt=""
                  />
                  <div className={styles.cach_list_name}>
                    {item.name && t(item.name)}
                  </div>
                </Link>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

export default CachedImage
